// Base Engine Class for Peak Priorities Elevation
// Implements the modular engine/operation architecture

export interface OperationSchema {
  id: string;
  name: string;
  description: string;
  inputSchema: string;
  outputSchema: string;
  example: string;
}

export interface EngineConfig {
  id: string;
  name: string;
  purpose: string;
  operations: OperationSchema[];
}

export abstract class BaseEngine {
  protected config: EngineConfig;
  protected operations: Map<string, Function>;

  constructor(config: EngineConfig) {
    this.config = config;
    this.operations = new Map();
    this.initializeOperations();
  }

  // Abstract method to be implemented by each engine
  protected abstract initializeOperations(): void;

  // Execute an operation
  public async executeOperation(operationId: string, input: any): Promise<any> {
    const operation = this.operations.get(operationId);
    if (!operation) {
      throw new Error(`Operation ${operationId} not found in ${this.config.name}`);
    }
    
    try {
      return await operation(input);
    } catch (error) {
      console.error(`Error executing ${operationId} in ${this.config.name}:`, error);
      throw error;
    }
  }

  // Get all available operations
  public getOperations(): OperationSchema[] {
    return this.config.operations;
  }

  // Get engine info
  public getInfo(): EngineConfig {
    return this.config;
  }

  // Validate input against operation schema
  protected validateInput(operationId: string, input: any): boolean {
    // Implementation would validate against the operation's input schema
    // This is a simplified version
    return true;
  }

  // Log operation execution
  protected logOperation(operationId: string, input: any, output: any): void {
    console.log(`[${this.config.name}] Executed ${operationId}:`, {
      input: typeof input,
      output: typeof output,
      timestamp: new Date().toISOString()
    });
  }
}
